import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, ExternalLink, ShoppingBag, BarChart3, TrendingUp, Eye, Package, ListChecks, Send, Flame, CheckCircle2, BellRing, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAdminAuth } from "@/context/AdminAuthContext";
import {
  adminListProducts,
  adminUpdateProduct,
  adminDeleteProduct,
  adminListGuides,
  adminDeleteGuide,
  adminListOrders,
  adminUpdateOrder,
  adminDeleteOrder,
  adminReports,
  adminAnalytics,
  adminPriceStatus,
  useSite,
} from "@/lib/api";
import { formatPrice } from "@/lib/site";
import ProductEditDialog from "@/components/admin/ProductEditDialog";
import GuideEditDialog from "@/components/admin/GuideEditDialog";
import SiteSettingsForm from "@/components/admin/SiteSettingsForm";
import CategoryManager from "@/components/admin/CategoryManager";
import PackageManager from "@/components/admin/PackageManager";
import TrustManager from "@/components/admin/TrustManager";
import QuickPriceManager from "@/components/admin/QuickPriceManager";
import MarketPriceManager from "@/components/admin/MarketPriceManager";
import MarketPriceHistory from "@/components/admin/MarketPriceHistory";
import MarketImageManager from "@/components/admin/MarketImageManager";
import OrderReceipt from "@/components/admin/OrderReceipt";

const ORDER_STATUS = {
  new: { label: "Yeni", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  contacted: { label: "İletişimde", cls: "text-amber-700 bg-amber-50 border-amber-200" },
  completed: { label: "Tamamlandı", cls: "text-charcoal/70 bg-charcoal/5 border-charcoal/15" },
  cancelled: { label: "İptal", cls: "text-red-700 bg-red-50 border-red-200" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const RankList = ({ title, icon: Icon, items, valueKey, suffix = "", testid }) => (
  <div data-testid={testid} className="border border-charcoal/10 bg-white">
    <div className="flex items-center gap-2 border-b border-charcoal/10 bg-[#faf7f2] px-4 py-3">
      <Icon className="h-4 w-4 text-bordo" />
      <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/70">{title}</span>
    </div>
    {items.length === 0 ? (
      <p className="px-4 py-6 text-center font-mono text-xs text-charcoal/40">Henüz veri yok</p>
    ) : (
      <ul>
        {items.map((it, i) => (
          <li key={it.slug} className="flex items-center gap-3 border-b border-charcoal/5 px-4 py-2.5 last:border-0">
            <span className="w-5 font-mono text-sm font-bold text-bordo/70">{i + 1}</span>
            {it.image ? (
              <img src={it.image} alt="" className="h-9 w-9 rounded object-cover" />
            ) : (
              <span className="h-9 w-9 rounded bg-charcoal/10" />
            )}
            <span className="flex-1 truncate font-serif text-base text-charcoal">{it.name}</span>
            <span className="font-mono text-sm font-bold text-charcoal/70">
              {it[valueKey] || 0}{suffix}
            </span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const RANGE_OPTIONS = [
  { value: "7", label: "Son 7 Gün" },
  { value: "30", label: "Son 30 Gün" },
  { value: "90", label: "Son 3 Ay" },
  { value: "365", label: "Son 1 Yıl" },
  { value: "0", label: "Tüm Zamanlar" },
];

const FUNNEL_STEPS = [
  { key: "visit", label: "Ziyaret", color: "bg-charcoal" },
  { key: "scenario", label: "Senaryo Açıldı", color: "bg-copper" },
  { key: "list", label: "Liste Oluşturuldu", color: "bg-bordo" },
  { key: "whatsapp", label: "WhatsApp'a Gönderildi", color: "bg-emerald-600" },
];

const Funnel = ({ funnel }) => {
  const top = funnel?.visit || 0;
  return (
    <div data-testid="conversion-funnel" className="border border-charcoal/10 bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-bordo" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/70">Dönüşüm Hunisi</span>
      </div>
      <div className="space-y-3">
        {FUNNEL_STEPS.map((s, i) => {
          const val = funnel?.[s.key] || 0;
          const pctOfTop = top > 0 ? Math.round((val / top) * 100) : 0;
          const prev = i === 0 ? val : funnel?.[FUNNEL_STEPS[i - 1].key] || 0;
          const delta = prev > 0 ? Math.round((val / prev) * 100) - 100 : 0;
          const deltaLabel = delta === 0 ? "→ 0%" : delta > 0 ? `↑ ${delta}%` : `↓ ${Math.abs(delta)}%`;
          const deltaCls = delta > 0 ? "text-emerald-600" : "text-charcoal/45";
          return (
            <div key={s.key} data-testid={`funnel-step-${s.key}`}>
              <div className="mb-1 flex items-center justify-between">
                <span className="font-serif text-base text-charcoal">{s.label}</span>
                <span className="font-mono text-sm font-bold text-charcoal">
                  {val}
                  {i > 0 && <span className={`ml-2 text-xs font-normal ${deltaCls}`}>{deltaLabel}</span>}
                </span>
              </div>
              <div className="h-7 w-full overflow-hidden rounded-sm bg-charcoal/5">
                <div
                  className={`flex h-full items-center ${s.color} transition-all duration-500`}
                  style={{ width: `${val > 0 ? Math.min(100, Math.max(pctOfTop, 8)) : 0}%` }}
                >
                  <span className="px-2 font-mono text-[10px] font-bold text-white/90">{pctOfTop}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, icon: Icon, testid }) => (
  <div data-testid={testid} className="border border-charcoal/10 bg-white p-5">
    <Icon className="h-5 w-5 text-bordo" />
    <p className="mt-3 truncate font-serif text-2xl text-charcoal sm:text-3xl">{value}</p>
    {sub ? <p className="truncate font-mono text-[11px] text-charcoal/45">{sub}</p> : null}
    <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-charcoal/50">{label}</p>
  </div>
);

const MiniChart = ({ series = [], granularity }) => {
  const max = Math.max(1, ...series.flatMap((d) => [d.visits, d.lists, d.sends]));
  return (
    <div data-testid="analytics-chart" className="border border-charcoal/10 bg-white p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-bordo" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/70">Zaman Serisi</span>
        </div>
        <div className="flex items-center gap-3 font-mono text-[10px] text-charcoal/60">
          <span className="flex items-center gap-1"><span className="h-2 w-2 bg-charcoal" /> Ziyaret</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 bg-bordo" /> Liste</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 bg-emerald-600" /> Gönderim</span>
        </div>
      </div>
      {series.length === 0 ? (
        <p className="py-10 text-center font-mono text-xs text-charcoal/40">Henüz veri yok</p>
      ) : (
        <div className="flex items-end gap-1.5 overflow-x-auto pb-1" style={{ height: 170 }}>
          {series.map((d) => (
            <div
              key={d.bucket}
              className="flex min-w-[26px] flex-1 flex-col items-center gap-1.5"
              title={`${d.bucket} · Ziyaret ${d.visits} · Liste ${d.lists} · Gönderim ${d.sends}`}
            >
              <div className="flex h-[120px] w-full items-end justify-center gap-0.5">
                <div className="w-1.5 bg-charcoal" style={{ height: `${(d.visits / max) * 100}%` }} />
                <div className="w-1.5 bg-bordo" style={{ height: `${(d.lists / max) * 100}%` }} />
                <div className="w-1.5 bg-emerald-600" style={{ height: `${(d.sends / max) * 100}%` }} />
              </div>
              <span className="truncate font-mono text-[8px] text-charcoal/40">
                {granularity === "daily" ? d.bucket.slice(8) : d.bucket.slice(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAdminAuth();
  const qc = useQueryClient();
  const [prodDialog, setProdDialog] = useState(null);
  const [guideDialog, setGuideDialog] = useState(null);
  const [range, setRange] = useState("7");
  const [tab, setTab] = useState("products");
  const [printOrder, setPrintOrder] = useState(null);
  const { data: site } = useSite();

  // Fiş yazdırma: sipariş seçilince barkod render olsun diye kısa bekleyip yazdır.
  useEffect(() => {
    if (!printOrder) return;
    const t = setTimeout(() => window.print(), 350);
    const after = () => setPrintOrder(null);
    window.addEventListener("afterprint", after);
    return () => { clearTimeout(t); window.removeEventListener("afterprint", after); };
  }, [printOrder]);

  const { data: products = [] } = useQuery({
    queryKey: ["admin", "products"],
    queryFn: adminListProducts,
    retry: false,
  });
  const { data: priceStatus } = useQuery({
    queryKey: ["admin", "price-status"],
    queryFn: adminPriceStatus,
    retry: false,
  });
  const { data: guides = [] } = useQuery({
    queryKey: ["admin", "guides"],
    queryFn: adminListGuides,
    retry: false,
  });
  const { data: orders = [] } = useQuery({
    queryKey: ["admin", "orders"],
    queryFn: adminListOrders,
    retry: false,
  });
  const { data: reports } = useQuery({
    queryKey: ["admin", "reports"],
    queryFn: adminReports,
    retry: false,
  });
  const { data: analytics } = useQuery({
    queryKey: ["admin", "analytics", range],
    queryFn: () => adminAnalytics(Number(range)),
    retry: false,
  });
  const reloadProducts = () => qc.invalidateQueries({ queryKey: ["admin", "products"] });
  const reloadGuides = () => qc.invalidateQueries({ queryKey: ["admin", "guides"] });
  const reloadOrders = () => {
    qc.invalidateQueries({ queryKey: ["admin", "orders"] });
    qc.invalidateQueries({ queryKey: ["admin", "reports"] });
  };

  const setOrderStatus = async (id, status) => {
    try {
      await adminUpdateOrder(id, status);
      toast.success("Sipariş durumu güncellendi");
      reloadOrders();
    } catch {
      toast.error("Güncellenemedi");
    }
  };
  const removeOrder = async (id) => {
    if (!window.confirm("Bu siparişi silmek istediğinize emin misiniz?")) return;
    try {
      await adminDeleteOrder(id);
      toast.success("Sipariş silindi");
      reloadOrders();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const toggleField = async (slug, field, value) => {
    try {
      await adminUpdateProduct(slug, { [field]: value });
      toast.success("Güncellendi");
      reloadProducts();
    } catch {
      toast.error("Güncellenemedi");
    }
  };
  const removeProduct = async (slug) => {
    if (!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    try {
      await adminDeleteProduct(slug);
      toast.success("Ürün silindi");
      reloadProducts();
    } catch {
      toast.error("Silinemedi");
    }
  };
  const removeGuide = async (slug) => {
    if (!window.confirm("Bu rehberi silmek istediğinize emin misiniz?")) return;
    try {
      await adminDeleteGuide(slug);
      toast.success("Rehber silindi");
      reloadGuides();
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="min-h-screen bg-[#f3efe8]">
      <header className="border-b border-charcoal/10 bg-charcoal text-cream">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <div>
            <h1 className="font-serif text-2xl">Ertan Kasap · Yönetim</h1>
            <p className="font-mono text-[10px] uppercase tracking-widest text-copper">
              Hoş geldin, {user?.name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 border border-cream/20 px-3 py-2 font-mono text-[11px] uppercase tracking-wide text-cream/80 transition-colors hover:bg-cream/10"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Siteyi Gör
            </a>
            <Button
              onClick={logout}
              data-testid="admin-logout"
              variant="ghost"
              className="text-cream hover:bg-cream/10 hover:text-cream"
            >
              <LogOut className="mr-1.5 h-4 w-4" /> Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {priceStatus && (
          <button
            type="button"
            onClick={() => setTab("quick-price")}
            data-testid="price-freshness-banner"
            className={`mb-5 flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
              priceStatus.is_fresh
                ? "border-emerald-200 bg-emerald-50 hover:bg-emerald-100/70"
                : "border-amber-300 bg-amber-50 hover:bg-amber-100/70"
            }`}
          >
            <div className="flex items-center gap-3">
              <span className={`flex h-9 w-9 items-center justify-center rounded-full ${priceStatus.is_fresh ? "bg-emerald-500" : "bg-amber-500"} text-white`}>
                {priceStatus.is_fresh ? <CheckCircle2 className="h-5 w-5" /> : <BellRing className="h-5 w-5" />}
              </span>
              <div>
                <div className="text-sm font-semibold text-charcoal">
                  {priceStatus.is_fresh
                    ? `Fiyatlar bugün güncellendi (${priceStatus.updated_today} ürün)`
                    : priceStatus.last_update
                    ? `Fiyatlar bugün güncellenmedi · son güncelleme ${priceStatus.days_since} gün önce`
                    : "Fiyatlar henüz hiç güncellenmedi"}
                </div>
                <div className="font-mono text-[11px] uppercase tracking-wide text-charcoal/50">
                  Hızlı Fiyat Güncelleme'ye git →
                </div>
              </div>
            </div>
            {!priceStatus.is_fresh && (
              <span className="hidden shrink-0 rounded-full bg-amber-500 px-3 py-1 text-xs font-medium text-white sm:inline">
                Güncelle
              </span>
            )}
          </button>
        )}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="products" data-testid="tab-products">
              Ürünler ({products.length})
            </TabsTrigger>
            <TabsTrigger value="quick-price" data-testid="tab-quick-price">
              Hızlı Fiyat
            </TabsTrigger>
            <TabsTrigger value="market-price" data-testid="tab-market-price">
              Market Fiyat
            </TabsTrigger>
            <TabsTrigger value="market-history" data-testid="tab-market-history">
              Fiyat Geçmişi
            </TabsTrigger>
            <TabsTrigger value="market-images" data-testid="tab-market-images">
              Market Görsel
            </TabsTrigger>
            <TabsTrigger value="packages" data-testid="tab-packages">
              Paketler
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">
              Kategoriler
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">
              Siparişler ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              Analitik
            </TabsTrigger>
            <TabsTrigger value="site" data-testid="tab-site">
              Site Ayarları
            </TabsTrigger>
            <TabsTrigger value="guides" data-testid="tab-guides">
              Rehber ({guides.length})
            </TabsTrigger>
            <TabsTrigger value="trust" data-testid="tab-trust">
              Güven Merkezi
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-6">
            <div className="mb-4 flex justify-end">
              <Button
                data-testid="add-product-btn"
                onClick={() => setProdDialog({ mode: "create" })}
                className="bg-bordo text-cream hover:bg-bordo-dark"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Yeni Ürün
              </Button>
            </div>
            <div className="overflow-x-auto border border-charcoal/10 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-charcoal/10 bg-[#faf7f2] font-mono text-[10px] uppercase tracking-wider text-charcoal/50">
                  <tr>
                    <th className="p-3">Ürün</th>
                    <th className="p-3">Kategori</th>
                    <th className="p-3">Fiyat (₺/kg)</th>
                    <th className="p-3 text-center">Taze</th>
                    <th className="p-3 text-center">Öne Çıkan</th>
                    <th className="p-3 text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr
                      key={p.slug}
                      data-testid={`admin-product-row-${p.slug}`}
                      className="border-b border-charcoal/5 hover:bg-[#faf7f2]"
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          {p.image ? (
                            <img src={p.image} alt="" className="h-10 w-10 rounded object-cover" />
                          ) : (
                            <span className="flex h-10 w-10 items-center justify-center rounded bg-charcoal/10 font-mono text-[9px] text-charcoal/40">—</span>
                          )}
                          <span className="font-serif text-base text-charcoal">{p.name}</span>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-xs text-charcoal/60">{p.category}</td>
                      <td className="p-3 font-mono font-bold text-bordo">
                        {p.price != null ? formatPrice(p.price) : "—"}
                      </td>
                      <td className="p-3 text-center">
                        <Switch
                          checked={!!p.fresh_cut}
                          onCheckedChange={(v) => toggleField(p.slug, "fresh_cut", v)}
                          data-testid={`toggle-fresh-${p.slug}`}
                        />
                      </td>
                      <td className="p-3 text-center">
                        <Switch
                          checked={!!p.featured}
                          onCheckedChange={(v) => toggleField(p.slug, "featured", v)}
                          data-testid={`toggle-featured-${p.slug}`}
                        />
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1.5">
                          <button
                            onClick={() => setProdDialog({ mode: "edit", product: p })}
                            data-testid={`edit-product-${p.slug}`}
                            className="rounded p-2 text-charcoal/60 transition-colors hover:bg-bordo/10 hover:text-bordo"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => removeProduct(p.slug)}
                            data-testid={`delete-product-${p.slug}`}
                            className="rounded p-2 text-charcoal/60 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-6">
            {orders.length === 0 ? (
              <div className="border border-dashed border-charcoal/20 bg-white py-20 text-center font-mono text-sm text-charcoal/40">
                Henüz sipariş gönderilmemiş. Müşteriler WhatsApp’a yönlendirildiğinde siparişler burada görünür.
              </div>
            ) : (
              <div className="grid gap-3">
                {orders.map((o) => {
                  const st = ORDER_STATUS[o.status] || ORDER_STATUS.new;
                  return (
                    <div
                      key={o.id}
                      data-testid={`admin-order-row-${o.id}`}
                      className="border border-charcoal/10 bg-white p-4"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-dashed border-charcoal/10 pb-3">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${st.cls}`}>
                            {st.label}
                          </span>
                          <span className="font-mono text-xs font-bold text-charcoal/80">{o.order_no || `#${o.id.slice(0, 8)}`}</span>
                          <span className="font-mono text-xs text-charcoal/50">{fmtDate(o.created_at)}</span>
                          {o.customer_name && <span className="text-xs font-medium text-charcoal/80">· {o.customer_name}</span>}
                          {o.phone && <span className="font-mono text-xs text-charcoal/50">· {o.phone}</span>}
                          {o.fulfillment === "pickup" ? (
                            <span className="rounded bg-charcoal/5 px-1.5 py-0.5 text-[10px] text-charcoal/60">Dükkândan Al</span>
                          ) : o.zone ? (
                            <span className="rounded bg-charcoal/5 px-1.5 py-0.5 text-[10px] text-charcoal/60">{o.zone}</span>
                          ) : null}
                          {o.payment && (
                            <span className="rounded border border-copper/30 bg-copper/5 px-1.5 py-0.5 text-[10px] text-copper">
                              {o.payment === "card" ? "Kapıda Kart" : "Kapıda Nakit"}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setPrintOrder(o)}
                            data-testid={`print-order-${o.id}`}
                            className="flex items-center gap-1.5 rounded border border-charcoal/15 px-2.5 py-1.5 text-xs font-medium text-charcoal/70 transition-colors hover:border-copper hover:text-copper"
                          >
                            <Printer className="h-3.5 w-3.5" /> Fiş Yazdır
                          </button>
                          <Select value={o.status} onValueChange={(v) => setOrderStatus(o.id, v)}>
                            <SelectTrigger data-testid={`order-status-${o.id}`} className="h-8 w-[150px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(ORDER_STATUS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <button
                            onClick={() => removeOrder(o.id)}
                            data-testid={`delete-order-${o.id}`}
                            className="rounded p-2 text-charcoal/50 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {o.address && o.fulfillment !== "pickup" && (
                        <p className="mt-2 text-xs text-charcoal/60">
                          <span className="font-medium text-charcoal/80">Adres:</span> {o.address}
                          {o.delivery_time ? ` · ${o.delivery_time}` : ""}
                        </p>
                      )}
                      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
                        {o.items.map((it, idx) => (
                          <li key={idx} className="font-mono text-sm text-charcoal/80">
                            {it.name} <span className="font-bold text-bordo">· {it.quantity}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-serif text-2xl text-charcoal">Ölçüm & Dönüşüm Paneli</h2>
              <Select value={range} onValueChange={setRange}>
                <SelectTrigger data-testid="analytics-range" className="h-11 w-[160px] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-3">
              <StatCard
                testid="stat-lists-created"
                label="Oluşturulan Liste"
                value={analytics?.summary?.lists_created ?? 0}
                icon={ListChecks}
              />
              <StatCard
                testid="stat-whatsapp-sends"
                label="WhatsApp Gönderimi"
                value={analytics?.summary?.whatsapp_sends ?? 0}
                icon={Send}
              />
              <StatCard
                testid="stat-avg-order"
                label="Ort. Sepet Değeri"
                value={analytics?.summary?.avg_order_value ? formatPrice(analytics.summary.avg_order_value) : "—"}
                icon={TrendingUp}
              />
              <StatCard
                testid="stat-top-product"
                label="En Çok Eklenen Ürün"
                value={analytics?.summary?.most_added?.label || "—"}
                sub={analytics?.summary?.most_added ? `${analytics.summary.most_added.count} kez` : ""}
                icon={ShoppingBag}
              />
              <StatCard
                testid="stat-top-scenario"
                label="En Çok Kullanılan Senaryo"
                value={analytics?.summary?.most_scenario?.label || "—"}
                sub={analytics?.summary?.most_scenario ? `${analytics.summary.most_scenario.count} kez` : ""}
                icon={Flame}
              />
              <StatCard
                testid="stat-top-package"
                label="En Çok Seçilen Paket"
                value={analytics?.summary?.most_package?.label || "—"}
                sub={analytics?.summary?.most_package ? `${analytics.summary.most_package.count} kez` : ""}
                icon={Package}
              />
            </div>

            <div className="mb-8 grid gap-4 lg:grid-cols-2">
              <Funnel funnel={analytics?.funnel} />
              <MiniChart series={analytics?.series || []} granularity={analytics?.granularity} />
            </div>

            <h3 className="mb-3 font-mono text-[11px] uppercase tracking-wider text-charcoal/50">
              Tüm Zamanlar Sıralamaları
            </h3>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <RankList
                testid="rank-most-added"
                title="En Çok Listeye Eklenen"
                icon={TrendingUp}
                items={reports?.most_added || []}
                valueKey="add_count"
              />
              <RankList
                testid="rank-most-viewed"
                title="En Çok Görüntülenen"
                icon={Eye}
                items={reports?.most_viewed || []}
                valueKey="view_count"
              />
              <RankList
                testid="rank-most-ordered"
                title="En Çok Siparişe Dönüşen"
                icon={ShoppingBag}
                items={reports?.most_ordered || []}
                valueKey="order_count"
              />
              <RankList
                testid="rank-most-packages"
                title="En Popüler Paketler"
                icon={Package}
                items={reports?.most_packages || []}
                valueKey="order_count"
              />
            </div>
          </TabsContent>

          <TabsContent value="packages" className="mt-6">
            <PackageManager />
          </TabsContent>

          <TabsContent value="quick-price" className="mt-6">
            <QuickPriceManager />
          </TabsContent>

          <TabsContent value="market-price" className="mt-6">
            <MarketPriceManager />
          </TabsContent>

          <TabsContent value="market-history" className="mt-6">
            <MarketPriceHistory />
          </TabsContent>

          <TabsContent value="market-images" className="mt-6">
            <MarketImageManager />
          </TabsContent>

          <TabsContent value="categories" className="mt-6">
            <CategoryManager />
          </TabsContent>

          <TabsContent value="trust" className="mt-6">
            <TrustManager />
          </TabsContent>

          <TabsContent value="site" className="mt-6">
            <SiteSettingsForm />
          </TabsContent>

          <TabsContent value="guides" className="mt-6">
            <div className="mb-4 flex justify-end">
              <Button
                data-testid="add-guide-btn"
                onClick={() => setGuideDialog({ mode: "create" })}
                className="bg-bordo text-cream hover:bg-bordo-dark"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Yeni Rehber
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {guides.map((g) => (
                <div
                  key={g.slug}
                  data-testid={`admin-guide-row-${g.slug}`}
                  className="flex items-center gap-3 border border-charcoal/10 bg-white p-3"
                >
                  {g.image ? (
                    <img src={g.image} alt="" className="h-14 w-14 rounded object-cover" />
                  ) : (
                    <span className="flex h-14 w-14 items-center justify-center rounded bg-charcoal/10 font-mono text-[9px] text-charcoal/40">—</span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-serif text-lg text-charcoal">{g.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-copper">{g.category}</p>
                  </div>
                  <button
                    onClick={() => setGuideDialog({ mode: "edit", guide: g })}
                    data-testid={`edit-guide-${g.slug}`}
                    className="rounded p-2 text-charcoal/60 transition-colors hover:bg-bordo/10 hover:text-bordo"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeGuide(g.slug)}
                    data-testid={`delete-guide-${g.slug}`}
                    className="rounded p-2 text-charcoal/60 transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {prodDialog && (
        <ProductEditDialog
          state={prodDialog}
          onClose={() => setProdDialog(null)}
          onSaved={() => {
            setProdDialog(null);
            reloadProducts();
          }}
        />
      )}
      {guideDialog && (
        <GuideEditDialog
          state={guideDialog}
          onClose={() => setGuideDialog(null)}
          onSaved={() => {
            setGuideDialog(null);
            reloadGuides();
          }}
        />
      )}

      {/* Termal fiş yazdırma katmanı (@media print ile yalnızca bu görünür) */}
      <div className="print-only" aria-hidden>
        {printOrder && <OrderReceipt order={printOrder} site={site} />}
      </div>
    </div>
  );
}
